import { useState, useEffect, useRef, useCallback } from "react";
import Hls from "hls.js";
import { api, TOKEN_KEY } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Attend la fin de la collecte ICE (ou timeout) — offre WHEP non-trickle plus fiable.
function waitIceGathering(pc, timeoutMs) {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") return resolve();
    let done = false;
    const finish = () => { if (!done) { done = true; pc.removeEventListener("icegatheringstatechange", check); resolve(); } };
    const check = () => { if (pc.iceGatheringState === "complete") finish(); };
    pc.addEventListener("icegatheringstatechange", check);
    setTimeout(finish, timeoutMs);
  });
}

// Résout true dès que la connexion WebRTC est établie, false si échec/timeout.
function waitConnected(pc, timeoutMs) {
  return new Promise((resolve) => {
    if (pc.connectionState === "connected") return resolve(true);
    let done = false;
    const finish = (v) => { if (!done) { done = true; pc.removeEventListener("connectionstatechange", check); resolve(v); } };
    const check = () => {
      if (pc.connectionState === "connected") finish(true);
      else if (["failed", "closed", "disconnected"].includes(pc.connectionState)) finish(false);
    };
    pc.addEventListener("connectionstatechange", check);
    setTimeout(() => finish(pc.connectionState === "connected"), timeoutMs);
  });
}

// États UX (7) attendus par le pilotage manuel
export const VIDEO_UI = {
  inactive: { label: "Vidéo inactive", color: "#A3A3A3" },
  starting: { label: "Démarrage caméra…", color: "#3B82F6" },
  mediamtx_starting: { label: "MediaMTX en cours de démarrage", color: "#3B82F6" },
  connected: { label: "Flux vidéo connecté", color: "#22C55E" },
  unavailable: { label: "Flux vidéo indisponible", color: "#A3A3A3" },
  raspberry_unreachable: { label: "Raspberry injoignable", color: "#EF4444" },
  camera_unavailable: { label: "Caméra indisponible", color: "#F59E0B" },
};

// WEBXR.8D-QA.3 — reconnexion automatique CLIENT : nouvelle session WHEP/HLS uniquement (jamais
// /video/start|restart, jamais systemctl). Backoff borné ; remis à zéro dès qu'une image est lue.
export const VIDEO_RETRY_DELAYS_MS = Object.freeze([3000, 5000, 10000, 20000, 30000]);

export function useVideoFeed(droneId, { autoStart = false, autoReconnect = false } = {}) {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const hlsRef = useRef(null);
  const watchdogRef = useRef(null);
  const playingRef = useRef(false);
  const autoStartedRef = useRef(false);
  const recoverRef = useRef(0); // tentatives de récupération HLS avant abandon
  const whepResourceRef = useRef(null); // ressource de session WHEP (pour DELETE)

  const [cfg, setCfg] = useState(null);
  const [health, setHealth] = useState(null);
  const [playMode, setPlayMode] = useState(null);
  const [playError, setPlayError] = useState(null);
  const [failReason, setFailReason] = useState(null); // mixed_content|webrtc_failed|hls_failed|network|cors
  const [metrics, setMetrics] = useState({ fps: null, latencyMs: null });
  const [connecting, setConnecting] = useState(false);
  const [service, setService] = useState({ pending: false, action: null, error: null, unreachable: false });

  // Sélecteur de mode + télémétrie flux vidéo (sparkline, reconnexions, dernier frame, stats).
  const prefRef = useRef((typeof localStorage !== "undefined" && localStorage.getItem("video_pref_mode")) || "webrtc");
  const [preferredMode, setPreferredModeState] = useState(prefRef.current);
  const [reconnections, setReconnections] = useState(0);
  const [latencyHistory, setLatencyHistory] = useState([]);
  const [lastFrameAt, setLastFrameAt] = useState(null);
  const [videoStats, setVideoStats] = useState({ width: null, height: null, bitrateKbps: null });
  const reconnRef = useRef(0);
  const latHistRef = useRef([]);
  const lastFrameRef = useRef(null);
  const bytesRef = useRef({ bytes: 0, ts: 0 });
  const retryRef = useRef({ n: 0, nextAt: null });
  const [clientReconnects, setClientReconnects] = useState(0);
  const [retryInMs, setRetryInMs] = useState(null);

  const stopPlayback = useCallback(() => {
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    // Ferme la session WHEP côté relais (best-effort, non bloquant).
    if (whepResourceRef.current) {
      const token = (typeof localStorage !== "undefined" && localStorage.getItem(TOKEN_KEY)) || "";
      try {
        fetch(`${BACKEND}/api/drones/${droneId}/video/whep?resource=${encodeURIComponent(whepResourceRef.current)}`,
          { method: "DELETE", headers: token ? { Authorization: `Bearer ${token}` } : {} }).catch(() => {});
      } catch { /* ignore */ }
      whepResourceRef.current = null;
    }
    try { pcRef.current?.close(); } catch { /* ignore */ }
    pcRef.current = null;
    try { hlsRef.current?.destroy(); } catch { /* ignore */ }
    hlsRef.current = null;
    if (videoRef.current) { videoRef.current.srcObject = null; videoRef.current.removeAttribute("src"); }
    playingRef.current = false;
    setPlayMode(null);
  }, [droneId]);

  const loadCfg = useCallback(async () => {
    try { const { data } = await api.get(`/drones/${droneId}/video`); setCfg(data.video); }
    catch { setCfg({ enabled: false }); }
  }, [droneId]);

  const loadHealth = useCallback(async () => {
    try { const { data } = await api.get(`/drones/${droneId}/video/status`); setHealth(data); return data; }
    catch { setHealth(null); return null; }
  }, [droneId]);

  useEffect(() => { loadCfg(); }, [loadCfg]);
  useEffect(() => {
    if (!cfg?.enabled) return;
    loadHealth();
    const id = setInterval(loadHealth, 15000);
    return () => clearInterval(id);
  }, [cfg?.enabled, loadHealth]);

  // Client WHEP (WebRTC) : offre SDP non-trickle -> proxy backend -> relais Hetzner.
  // Le navigateur ne contacte JAMAIS 10.50.0.10 ; le média circule via le relais public.
  const connectWebRTC = useCallback(async () => {
    if (!cfg?.webrtc_url) return false;
    const video = videoRef.current;
    const token = (typeof localStorage !== "undefined" && localStorage.getItem(TOKEN_KEY)) || "";
    let pc = null;
    try {
      pc = new RTCPeerConnection({ iceServers: [] });
      pcRef.current = pc;
      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });
      pc.ontrack = (e) => {
        if (video && e.streams && e.streams[0]) { video.srcObject = e.streams[0]; video.play?.().catch(() => {}); }
      };
      pc.onconnectionstatechange = () => {
        if (["disconnected", "failed"].includes(pc.connectionState)) {
          reconnRef.current += 1; setReconnections(reconnRef.current);
        }
        // Perte d'une session établie : lecteur marqué arrêté -> la reconnexion auto prend le relais.
        if (autoReconnect && pc.connectionState === "failed" && pcRef.current === pc && playingRef.current) {
          playingRef.current = false; setPlayMode(null); setFailReason("webrtc_failed");
        }
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitIceGathering(pc, 2000);
      const resp = await fetch(`${BACKEND}/api/drones/${droneId}/video/whep`, {
        method: "POST",
        headers: { "Content-Type": "application/sdp", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: pc.localDescription.sdp,
      });
      if (!resp.ok) throw new Error(`whep_${resp.status}`);
      const answer = await resp.text();
      whepResourceRef.current = resp.headers.get("X-WHEP-Resource") || null;
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
      const ok = await waitConnected(pc, 6000);
      if (!ok) throw new Error("ice_timeout");
      playingRef.current = true;
      setPlayMode("webrtc"); setPlayError(null); setFailReason(null); setConnecting(false);
      return true;
    } catch {
      try { pc?.close(); } catch { /* ignore */ }
      if (pcRef.current === pc) pcRef.current = null;
      whepResourceRef.current = null;
      return false;
    }
  }, [cfg, droneId, autoReconnect]);

  // Lecture HLS (fallback supervision) via le proxy HTTPS SkyCore.
  const connectHLS = useCallback(() => {
    const video = videoRef.current;
    const proxyUrl = `${BACKEND}/api/drones/${droneId}/video/hls/index.m3u8`;
    const token = (typeof localStorage !== "undefined" && localStorage.getItem(TOKEN_KEY)) || "";
    recoverRef.current = 0;
    watchdogRef.current = setTimeout(() => {
      if (!playingRef.current) { setPlayError("timeout"); setFailReason((r) => r || "network"); setConnecting(false); }
    }, 9000);
    try {
      if (Hls.isSupported()) {
        // Configuration LOW-LATENCY : vise la tête de flux (live edge) avec un buffer minimal.
        // Combiné à MediaMTX en mode LL-HLS (voir deploy/infra-api/MEDIAMTX_LOW_LATENCY.md),
        // la latence bout-en-bout descend de ~17 s vers ~2-4 s en supervision.
        const hls = new Hls({
          lowLatencyMode: true,
          liveSyncDuration: 1.5,          // cible : ~1.5 s derrière le live edge
          liveMaxLatencyDuration: 6,      // resync forcé au-delà de 6 s
          maxLiveSyncPlaybackRate: 1.5,   // accélère la lecture (jusqu'à x1.5) pour rattraper le direct
          liveDurationInfinity: true,
          backBufferLength: 4,            // ne conserve que 4 s d'historique
          maxBufferLength: 8,             // buffer avant minimal
          maxBufferHole: 0.3,
          manifestLoadingTimeOut: 8000,
          fragLoadingTimeOut: 12000,
          xhrSetup: (xhr) => { if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`); },
        });
        hls.loadSource(proxyUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => { video?.play?.().catch(() => {}); playingRef.current = true; recoverRef.current = 0; setPlayMode("hls"); setPlayError(null); setFailReason(null); setConnecting(false); });
        hls.on(Hls.Events.ERROR, (_e, d) => {
          if (!d.fatal) return;
          // Récupération propre : réseau -> relance du chargement ; média -> recoverMediaError.
          // Après 3 échecs consécutifs, on abandonne et on affiche une cause claire.
          if (recoverRef.current < 3) {
            recoverRef.current += 1;
            if (d.type === Hls.ErrorTypes.NETWORK_ERROR) { try { hls.startLoad(); return; } catch { /* ignore */ } }
            if (d.type === Hls.ErrorTypes.MEDIA_ERROR) { try { hls.recoverMediaError(); return; } catch { /* ignore */ } }
          }
          setPlayError("stream"); setFailReason("hls_failed"); setConnecting(false);
          try { hls.destroy(); } catch { /* ignore */ } hlsRef.current = null; setPlayMode(null); playingRef.current = false;
        });
        hlsRef.current = hls;
        return;
      }
      // Fallback natif (Safari) : le token ne peut pas être ajouté en en-tête -> lecture best-effort.
      if (video?.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = proxyUrl;
        video.addEventListener("loadedmetadata", () => { video.play?.().catch(() => {}); playingRef.current = true; setPlayMode("hls"); setConnecting(false); }, { once: true });
        video.addEventListener("error", () => { setPlayError("stream"); setFailReason("hls_failed"); setConnecting(false); }, { once: true });
        return;
      }
    } catch { /* ignore */ }
    setPlayError("stream");
    setFailReason("hls_failed");
    setConnecting(false);
  }, [droneId]);

  const connect = useCallback(async () => {
    if (!cfg?.enabled) return;
    stopPlayback();
    setPlayError(null);
    setFailReason(null);
    setConnecting(true);
    playingRef.current = false;
    // 1) WebRTC prioritaire (pilotage fin, sub-seconde). Si le relais Hetzner n'est pas
    //    encore déployé, l'offre échoue proprement -> bascule automatique HLS (supervision).
    if (cfg?.webrtc_url && prefRef.current !== "hls") {
      const ok = await connectWebRTC();
      if (ok) return;
    }
    // 2) Fallback HLS relayé HTTPS.
    connectHLS();
  }, [cfg, stopPlayback, connectWebRTC, connectHLS]);

  const setPreferredMode = useCallback((mode) => {
    prefRef.current = mode;
    setPreferredModeState(mode);
    try { localStorage.setItem("video_pref_mode", mode); } catch { /* ignore */ }
    if (cfg?.enabled) connect();
  }, [cfg, connect]);

  // Démarrage / arrêt du service MediaMTX à la demande (backend -> Infra API -> SSH)
  const controlService = useCallback(async (action = "start") => {
    setService({ pending: true, action, error: null, unreachable: false });
    try {
      const { data } = await api.post(`/drones/${droneId}/video/${action}`);
      if (data.ok) {
        // laisser MediaMTX démarrer, puis re-sonder + reconnecter
        await new Promise((r) => setTimeout(r, 2500));
        await loadHealth();
        if (action !== "stop") await connect();
        setService({ pending: false, action, error: null, unreachable: false });
      } else {
        const unreachable = data.status === 504;
        setService({ pending: false, action, error: data.not_deployed ? "probe_not_deployed" : (data.error || "Échec"), unreachable });
      }
    } catch (e) {
      setService({ pending: false, action, error: e?.response?.data?.detail || "Échec", unreachable: e?.response?.status === 504 });
    }
  }, [droneId, loadHealth, connect]);

  useEffect(() => () => stopPlayback(), [stopPlayback]);

  // Planificateur unique (1 Hz) : robuste au regroupement des mises à jour React ; ne lance une nouvelle
  // session que si le lecteur ne lit pas, qu'aucune tentative n'est en cours et que le délai est échu.
  const connectingRef = useRef(false);
  connectingRef.current = connecting;
  const pendingRef = useRef(false);
  pendingRef.current = service.pending;
  const resetRetry = () => { retryRef.current.n = 0; retryRef.current.nextAt = null; setRetryInMs(null); };
  // Réabonnement manuel (bouton RECONNECTER LES FLUX) : client uniquement, backoff remis à zéro.
  const reconnectStreams = useCallback(() => {
    resetRetry();
    setClientReconnects((n) => n + 1);
    if (cfg?.enabled) connect();
  }, [cfg?.enabled, connect]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!autoReconnect || !cfg?.enabled) return undefined;
    retryRef.current.nextAt = Date.now() + VIDEO_RETRY_DELAYS_MS[0];
    const id = setInterval(() => {
      const r = retryRef.current;
      if (playingRef.current) { if (r.n || r.nextAt) resetRetry(); return; }
      if (connectingRef.current || pendingRef.current) return;
      if (r.nextAt == null) {
        const d = VIDEO_RETRY_DELAYS_MS[Math.min(r.n, VIDEO_RETRY_DELAYS_MS.length - 1)];
        r.nextAt = Date.now() + d; setRetryInMs(d); return;
      }
      if (Date.now() < r.nextAt) return;
      r.n += 1; r.nextAt = null; setRetryInMs(null);
      setClientReconnects((n) => n + 1);
      connect();
    }, 1000);
    return () => clearInterval(id);
  }, [autoReconnect, cfg?.enabled, connect]); // eslint-disable-line react-hooks/exhaustive-deps
  // Retour réseau / onglet visible / flux annoncé disponible : nouvelle tentative immédiate.
  useEffect(() => {
    if (!autoReconnect) return undefined;
    const kick = () => { if (!playingRef.current && !connectingRef.current && cfg?.enabled && document.visibilityState !== "hidden") { resetRetry(); connect(); } };
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", kick);
    return () => { window.removeEventListener("online", kick); document.removeEventListener("visibilitychange", kick); };
  }, [autoReconnect, cfg?.enabled, connect]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoReconnect && health?.status === "connected" && retryRef.current.n > 0 && !playingRef.current && !connectingRef.current && cfg?.enabled) { resetRetry(); connect(); }
  }, [autoReconnect, health?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (cfg?.enabled) connect(); }, [cfg?.enabled]); // eslint-disable-line

  // Auto-start conditionnel : au montage, si MediaMTX est CLAIREMENT hors ligne (statut fiable via sonde),
  // on démarre une fois le service puis on re-sonde. Pas déclenché si statut ambigu (unknown/probe non déployée).
  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    if (health?.status === "mediamtx_offline") {
      autoStartedRef.current = true;
      controlService("start");
    }
  }, [autoStart, health?.status, controlService]);

  // Métriques temps réel (FPS + latence + résolution + bitrate + sparkline + dernier frame)
  useEffect(() => {
    if (!playMode) {
      setMetrics({ fps: null, latencyMs: null });
      setLatencyHistory([]); latHistRef.current = [];
      setLastFrameAt(null); lastFrameRef.current = null;
      setVideoStats({ width: null, height: null, bitrateKbps: null });
      bytesRef.current = { bytes: 0, ts: 0 };
      return;
    }
    const video = videoRef.current;
    const useRVFC = video && typeof video.requestVideoFrameCallback === "function";
    let frames = 0, t0 = performance.now(), rafId = null;
    const onFrame = () => { frames++; lastFrameRef.current = Date.now(); if (video?.requestVideoFrameCallback) rafId = video.requestVideoFrameCallback(onFrame); };
    if (useRVFC) rafId = video.requestVideoFrameCallback(onFrame);
    const id = setInterval(async () => {
      const now = performance.now();
      let fps = null;
      if (useRVFC) { const dt = (now - t0) / 1000; if (dt > 0) fps = Math.round(frames / dt); frames = 0; t0 = now; }
      let latencyMs = null, bitrateKbps = null;
      if (playMode === "webrtc" && pcRef.current) {
        try {
          const stats = await pcRef.current.getStats();
          stats.forEach((r) => {
            if (r.type === "inbound-rtp" && (r.kind === "video" || r.mediaType === "video")) {
              if (r.framesPerSecond != null) fps = Math.round(r.framesPerSecond);
              if (r.bytesReceived != null) {
                const prev = bytesRef.current;
                if (prev.ts > 0 && r.timestamp > prev.ts) {
                  bitrateKbps = Math.round((r.bytesReceived - prev.bytes) * 8 / (r.timestamp - prev.ts));
                }
                bytesRef.current = { bytes: r.bytesReceived, ts: r.timestamp };
              }
            }
            if (r.type === "candidate-pair" && r.nominated && r.currentRoundTripTime != null) latencyMs = Math.round(r.currentRoundTripTime * 1000);
          });
        } catch { /* ignore */ }
      }
      // HLS : latence réelle par rapport au live edge + estimation de bande passante.
      if (playMode === "hls" && hlsRef.current) {
        if (typeof hlsRef.current.latency === "number" && hlsRef.current.latency > 0) latencyMs = Math.round(hlsRef.current.latency * 1000);
        if (typeof hlsRef.current.bandwidthEstimate === "number" && hlsRef.current.bandwidthEstimate > 0) bitrateKbps = Math.round(hlsRef.current.bandwidthEstimate / 1000);
      }
      if (latencyMs == null) latencyMs = health?.latency_ms ?? null;
      setMetrics({ fps, latencyMs });
      setVideoStats({ width: video?.videoWidth || null, height: video?.videoHeight || null, bitrateKbps });
      if (latencyMs != null) {
        latHistRef.current = [...latHistRef.current, latencyMs].slice(-40);
        setLatencyHistory(latHistRef.current);
      }
      setLastFrameAt(lastFrameRef.current);
    }, 2000);
    return () => { clearInterval(id); if (useRVFC && rafId && video?.cancelVideoFrameCallback) { try { video.cancelVideoFrameCallback(rafId); } catch { /* ignore */ } } };
  }, [playMode, health?.latency_ms]);

  // Qualité du flux (excellent / moyen / dégradé) — seuils adaptés au protocole.
  // HLS a une latence intrinsèquement plus élevée que WebRTC : on juge sur la latence live-edge.
  const quality = (() => {
    const { fps, latencyMs } = metrics;
    if (fps == null && latencyMs == null) return null;
    const f = fps ?? 99, l = latencyMs ?? 0;
    if (playMode === "hls") {
      if (l > 0 && l <= 2500 && f >= 15) return "excellent";
      if (l > 0 && l <= 5000 && f >= 10) return "moyen";
      return "degrade";
    }
    if (f >= 20 && l < 90) return "excellent";
    if (f >= 12 && l < 220) return "moyen";
    return "degrade";
  })();

  // Diagnostic : joignable côté serveur mais non affichable côté navigateur ?
  const serverReachable = health?.status === "connected"
    || !!(health?.checks && (health.checks.webrtc?.reachable || health.checks.hls?.reachable || health.checks.rtsp?.reachable));
  const diagnostic = {
    serverReachable,
    clientPlayable: !!playMode,
    cause: (!playMode && serverReachable && !connecting && !service.pending) ? (failReason || "network") : null,
  };

  // Dérive l'état UX unique
  let uiState = "inactive";
  if (service.pending) uiState = service.action === "start" || service.action === "restart" ? "starting" : "inactive";
  else if (service.unreachable) uiState = "raspberry_unreachable";
  else if (playMode) uiState = "connected";
  else if (health?.status === "connected" && !failReason) uiState = "connected";
  else if (health?.status === "camera_unavailable") uiState = "camera_unavailable";
  else if (health?.status === "mediamtx_offline") uiState = "inactive";
  else if (connecting) uiState = "mediamtx_starting";
  else uiState = "unavailable";

  return { videoRef, cfg, health, playMode, connecting, playError, failReason, service,
           metrics, quality, diagnostic, connect, loadHealth, controlService, uiState,
           preferredMode, setPreferredMode, reconnections, latencyHistory, lastFrameAt, videoStats,
           reconnectStreams, clientReconnects, retryInMs };
}

export const QUALITY_META = {
  excellent: { label: "Excellent", color: "#22C55E" },
  moyen: { label: "Moyen", color: "#F59E0B" },
  degrade: { label: "Dégradé", color: "#EF4444" },
};

export const CAUSE_LABEL = {
  mixed_content: "Flux bloqué par le navigateur (HTTPS → HTTP). Le relais HLS SkyCore est utilisé automatiquement.",
  network: "Le relais média SkyCore est injoignable (Infra API / MediaMTX). Vérifiez que MediaMTX est démarré (« Activer vidéo »).",
  webrtc_failed: "WebRTC a échoué (WHEP).",
  hls_failed: "Le flux HLS relayé n'a pas pu être chargé — MediaMTX est peut-être arrêté ou le flux « cam » n'est pas publié.",
  cors: "Accès refusé par la politique CORS du serveur média.",
};
